import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { C, S, font, fontCond } from "../constants";
import { YellowTriangle } from "../components/ui";
import { RoundTimePicker } from "../lib/courtAvailability";
import { shortName, canChallenge, getBlockStatus, isChallengerBlocked } from "../lib/helpers";

// ── Tamanho dos cards da pirâmide ──────────────────────────────────────────────
// Mobile: tamanho fixo (a base sempre precisa de rolagem lateral no celular).
// Desktop: o card se ajusta à largura disponível, entre minW e maxW, para a base
// caber na tela sem rolagem horizontal. Ajuste os valores abaixo se quiser.
const MOBILE_CARD  = { w: 38, h: 47 };
const DESKTOP_CARD = { maxW: 70, minW: 40, ratio: 86 / 70 };
const CARD_GAP     = 3;

function mkAvatar(name) {
  const parts = name.trim().split(" ");
  const initStr = ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase();
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><rect width="40" height="40" fill="#dde3ea"/><text x="20" y="26" text-anchor="middle" font-family="Arial,sans-serif" font-size="15" font-weight="700" fill="#005095">${initStr}</text></svg>`;
  return "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svg)));
}

export default function PyramidScreen({
  pyramidData, PYRAMID_ROWS, sorted,
  me, isPublic, state,
  hoveredCell, setHoveredCell,
  pyramidModal, setPyramidModal, modalDates, setModalDates,
  pyramidTooltip, setPyramidTooltip,
  proposeFromPyramid,
  isMobile, allChallenges,
}) {
  const cellUnit = 75;
  const maxRowSize = PYRAMID_ROWS.reduce((mx, [a, b], i) => i < PYRAMID_ROWS.length - 1 ? Math.max(mx, b - a + 1) : mx, 0);
  const fullLastRowSize = maxRowSize + 2;

  const scrollRef = useRef(null);
  useEffect(() => {
    if (scrollRef.current) {
      const el = scrollRef.current;
      el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2;
    }
  }, []);

  // Largura disponível para a pirâmide → tamanho do card no desktop
  const [availW, setAvailW] = useState(0);
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => setAvailW(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isMobile]);

  const fitW  = availW > 0
    ? Math.floor((availW - 1 - (fullLastRowSize - 1) * CARD_GAP) / fullLastRowSize)
    : DESKTOP_CARD.maxW;
  const cardW = Math.max(DESKTOP_CARD.minW, Math.min(DESKTOP_CARD.maxW, fitW));
  const cardH = Math.round(cardW * DESKTOP_CARD.ratio);

  return (
    <div style={{ ...S.card, padding: isMobile ? 12 : 24 }}>
      <div style={S.title}><YellowTriangle size={16}/> Pirâmide do Ranking</div>

      {/* Legenda */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14, fontSize: 11 }}>
        {[{c:C.primary,l:"Você"},{c:C.accent,l:"Pode desafiar"},{c:"#c0152a",l:"Indisponível"},{c:"#555",l:"Inativo"},{c:C.border,l:"Fora do alcance"}].map(({c,l}) => (
          <span key={l} style={{ display: "flex", alignItems: "center", gap: 5, color: C.muted }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: c, display: "inline-block", flexShrink: 0 }}/>{l}
          </span>
        ))}
      </div>

      {isMobile && <div style={{ fontSize: 11, color: C.muted, marginBottom: 10, background: C.cardAlt, borderRadius: 8, padding: "6px 12px", textAlign: "center" }}>← Deslize para ver toda a pirâmide →</div>}

      <div ref={scrollRef} style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", marginLeft: isMobile ? -12 : 0, marginRight: isMobile ? -12 : 0, paddingLeft: isMobile ? 12 : 0, paddingRight: isMobile ? 12 : 0 }}>
        {isMobile
          ? <MobilePyramid pyramidData={pyramidData} me={me} isPublic={isPublic} state={state} setPyramidModal={setPyramidModal} setModalDates={setModalDates} pyramidTooltip={pyramidTooltip} setPyramidTooltip={setPyramidTooltip} missingEmail={!me?.email?.trim()}/>
          : <DesktopPyramid pyramidData={pyramidData} PYRAMID_ROWS={PYRAMID_ROWS} me={me} isPublic={isPublic} hoveredCell={hoveredCell} setHoveredCell={setHoveredCell} setPyramidModal={setPyramidModal} setModalDates={setModalDates} cellUnit={cellUnit} fullLastRowSize={fullLastRowSize} cardW={cardW} cardH={cardH} missingEmail={!me?.email?.trim()}/>
        }
      </div>

      {/* Modal de proposta de desafio via pirâmide */}
      {pyramidModal && (
        <div style={{ ...S.modal, alignItems: "center" }}>
          <div style={S.modalBox(isMobile)}>
            <div style={{ width: 40, height: 4, background: C.border, borderRadius: 2, margin: "0 auto 20px", display: isMobile ? "block" : "none" }}/>
            <div style={{ fontFamily: fontCond, fontSize: 20, fontWeight: 900, color: C.text, marginBottom: 4 }}>⚔️ Propor Desafio</div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>
              Você (#{me?.position}) desafia <b style={{ color: C.primary }}>{pyramidModal.name}</b> (#{pyramidModal.position})
            </div>
            <div style={{ background: C.cardAlt, border: "1px solid " + C.border, borderRadius: 8, padding: "10px 12px", marginBottom: 14, fontSize: 12, color: C.muted, lineHeight: 1.8 }}>
              <div style={{ fontWeight: 700, color: C.accent, marginBottom: 4, fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase" }}>📅 Quadras disponíveis</div>
              <div><b style={{ color: C.text }}>Seg–Sex:</b> 6h–16h / 19h–23h</div>
              <div><b style={{ color: C.text }}>Sáb:</b> 14h–16h (1q) · 18h+ (2q)</div>
              <div><b style={{ color: C.text }}>Dom:</b> 14h+ (2 quadras)</div>
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, color: C.muted, textTransform: "uppercase", marginBottom: 8 }}>3 opções de data e horário</div>
            {modalDates.map((d, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 12, color: C.muted, minWidth: 52, flexShrink: 0 }}>Opção {i+1}</span>
                <RoundTimePicker
                  value={d}
                  onChange={v => { const nd = [...modalDates]; nd[i] = v; setModalDates(nd); }}
                  style={{ ...S.input, marginBottom: 0 }}
                  allChallenges={allChallenges}
                  otherDates={modalDates.filter((_, j) => j !== i)}
                  blockedDates={state.blockedDates}
                  holidays={state.holidays || []}
                  courtSlots={state.courtSlots || []}
                  blockedPeriods={state.blockedPeriods || []}
                />
              </div>
            ))}
            <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
              <button style={{ ...S.btn(), flex: 1 }} onClick={proposeFromPyramid}>Enviar Desafio</button>
              <button style={{ ...S.btn(C.muted, true), flex: 1 }} onClick={() => { setPyramidModal(null); setModalDates(["","",""]); }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Mobile Pyramid ─────────────────────────────────────────────────────────────
function MobilePyramid({ pyramidData, me, isPublic, state, setPyramidModal, setModalDates, pyramidTooltip, setPyramidTooltip, missingEmail }) {
  const CW = MOBILE_CARD.w, CH = MOBILE_CARD.h, GAP = CARD_GAP;
  const maxN = Math.max(...pyramidData.map(r => r.length));
  const fullN = maxN + 2;
  const totalW = fullN * CW + (fullN - 1) * GAP;

  return (
    <div style={{ display: "inline-flex", flexDirection: "column", gap: GAP, paddingBottom: 8, paddingTop: 50, paddingRight: 12, minWidth: totalW, overflow: "visible" }}>
      {pyramidData.map((row, ri) => {
        const n = row.length, rowW = n * CW + (n - 1) * GAP;
        const isLast = ri === pyramidData.length - 1;
        let ml;
        if (isLast && ri >= 1) {
          const prevN = pyramidData[ri-1].length, prevRowW = prevN * CW + (prevN - 1) * GAP;
          const prevMl = Math.round((totalW - prevRowW) / 2);
          ml = Math.max(0, prevMl - (CW + GAP));
        } else { ml = Math.round((totalW - rowW) / 2); }

        const showBelow = ri === 0;

        return (
          <div key={ri} style={{ display: "flex", gap: GAP, marginLeft: ml, overflow: "visible" }}>
            {row.map(({p, bs, isMe, eligible}) => {
              let bg = C.cardAlt, brd = "1px solid " + C.border, shd = "none";
              if (isMe)               { bg = C.primary; brd = "2px solid #ff7090"; shd = "0 0 10px rgba(233,69,96,.45)"; }
              else if (bs === "inativo")   { bg = "#f0f0f5"; brd = "1px solid #c8c8d8"; }
              else if (bs === "bloqueado") { bg = "#f7e8e8"; brd = "2px solid #c0152a"; }
              else if (eligible)      { bg = "#e6f7f3"; brd = "2px solid " + C.accent; shd = "0 0 7px rgba(0,168,130,.25)"; }
              const canClick = eligible && !isPublic && !missingEmail;
              const isPopped = pyramidTooltip?.id === p.id;
              const blockUntil = bs === "bloqueado" ? p.blockedAsChallenged : bs === "inativo" ? p.inactiveUntil : null;

              return (
                <div key={p.id} style={{ position: "relative", flexShrink: 0 }}>
                  <button
                    onClick={e => {
                      e.currentTarget.blur();
                      if (canClick) { setModalDates(["","",""]); setPyramidModal(p); setPyramidTooltip(null); }
                      else {
                        if (isPopped) { setPyramidTooltip(null); return; }
                        setPyramidTooltip({ id: p.id, isMe, eligible, bs, name: p.name, position: p.position, missingEmail: eligible && missingEmail, blockUntil });
                      }
                    }}
                    style={{ width: CW, height: CH, background: bg, border: brd, borderRadius: 8, padding: 0, cursor: canClick ? "pointer" : "default", position: "relative", overflow: "hidden", boxSizing: "border-box", boxShadow: isPopped ? "0 0 0 2px " + C.primary + ", " + shd : shd, transform: isPopped ? "scale(1.08)" : "scale(1)", transition: "transform .15s", WebkitTapHighlightColor: "transparent" }}>
                    <img src={p.photo || mkAvatar(p.name)} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: bs === "inativo" ? .2 : .88 }}/>
                    <div style={{ position: "absolute", top: 2, right: 3, fontFamily: fontCond, fontSize: 10, fontWeight: 900, color: "#fff", textShadow: "0 1px 3px rgba(0,0,0,.9)", lineHeight: 1 }}>{p.position}</div>
                    <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "linear-gradient(transparent,rgba(0,0,0,.82))", color: "#fff", fontSize: 8, fontWeight: 700, padding: "8px 2px 3px", textAlign: "center", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", lineHeight: 1.2 }}>{shortName(p.name)}</div>
                    {bs === "bloqueado" && <div style={{ position: "absolute", top: 2, left: 3, width: 5, height: 5, borderRadius: "50%", background: "#c0152a", border: "1px solid #fff" }}/>}
                    {bs === "inativo"   && <div style={{ position: "absolute", top: 2, left: 3, width: 5, height: 5, borderRadius: "50%", background: "#999", border: "1px solid #fff" }}/>}
                    {eligible           && <div style={{ position: "absolute", top: 2, left: 3, width: 5, height: 5, borderRadius: "50%", background: C.accent, border: "1px solid #fff" }}/>}
                  </button>

                  {/* Tooltip inline — ancorado ao card, imune a scroll e zoom */}
                  {isPopped && (
                    <div style={{ position: "absolute", left: "50%", ...(showBelow ? { top: CH + 8 } : { bottom: CH + 8 }), transform: "translateX(-50%)", background: C.text, color: "#fff", borderRadius: 10, padding: "8px 12px", fontSize: 12, whiteSpace: "nowrap", zIndex: 9999, boxShadow: "0 4px 16px rgba(0,0,0,.4)", pointerEvents: "none", lineHeight: 1.6 }}>
                      <div style={{ fontWeight: 700 }}>#{p.position} {p.name}</div>
                      {isMe     && <div style={{ color: "#ff9fb5", fontSize: 11 }}>← Você</div>}
                      {eligible && !missingEmail && <div style={{ color: "#6ef0d0", fontSize: 11 }}>✓ Toque para desafiar</div>}
                      {eligible && missingEmail  && <div style={{ color: "#ffd080", fontSize: 11 }}>⚠️ Cadastre email para desafiar</div>}
                      {bs === "bloqueado" && <div style={{ color: "#ffb0b0", fontSize: 11 }}>🔒 Indisponível{blockUntil ? " até " + new Date(blockUntil).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }) : ""}</div>}
                      {bs === "inativo"   && <div style={{ color: "#ccc", fontSize: 11 }}>😴 Inativo{blockUntil ? " até " + new Date(blockUntil).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }) : ""}</div>}
                      {showBelow
                        ? <div style={{ position: "absolute", top: -6, left: "50%", transform: "translateX(-50%)", width: 0, height: 0, borderLeft: "6px solid transparent", borderRight: "6px solid transparent", borderBottom: "6px solid " + C.text }}/>
                        : <div style={{ position: "absolute", bottom: -6, left: "50%", transform: "translateX(-50%)", width: 0, height: 0, borderLeft: "6px solid transparent", borderRight: "6px solid transparent", borderTop: "6px solid " + C.text }}/>
                      }
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

// ── Desktop Pyramid ────────────────────────────────────────────────────────────
function DesktopPyramid({ pyramidData, PYRAMID_ROWS, me, isPublic, hoveredCell, setHoveredCell, setPyramidModal, setModalDates, cellUnit, fullLastRowSize, cardW, cardH, missingEmail }) {
  const CW = cardW, CH = cardH, GAP = CARD_GAP;
  const small = CW < 56;   // cards pequenos: fontes e bordas menores
  const totalW = fullLastRowSize * CW + (fullLastRowSize - 1) * GAP;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: GAP, width: totalW + "px", margin: "0 auto", minWidth: "fit-content", paddingTop: 50 }}>
      {pyramidData.map((row, ri) => {
        const rowSize = PYRAMID_ROWS[ri][1] - PYRAMID_ROWS[ri][0] + 1;
        const isLast = ri === pyramidData.length - 1;
        const rowMargin = (() => {
          if (isLast && ri >= 1) {
            const pSize = PYRAMID_ROWS[ri-1][1] - PYRAMID_ROWS[ri-1][0] + 1;
            const pRowW = pSize * CW + (pSize - 1) * GAP;
            const penultMargin = Math.round((totalW - pRowW) / 2);
            return Math.max(0, penultMargin - (CW + GAP));
          }
          const rowW = rowSize * CW + (rowSize - 1) * GAP;
          return Math.round((totalW - rowW) / 2);
        })();

        return (
          <div key={ri} style={{ display: "flex", gap: GAP, marginLeft: rowMargin + "px" }}>
            {row.map(({p, bs, isMe, eligible}) => {
              let bg = C.cardAlt, border = "1px solid " + C.border, shadow = "none";
              if (isMe)               { bg = C.primary; border = "2px solid #ff7090"; shadow = "0 0 14px rgba(233,69,96,.5)"; }
              else if (bs === "inativo")   { bg = "#f0f0f5"; border = "1px solid #c8c8d8"; }
              else if (bs === "bloqueado") { bg = "#f7e8e8"; border = (small ? "2px" : "3px") + " solid #c0152a"; }
              else if (eligible)      { bg = "#e6f7f3"; border = "2px solid " + C.accent; shadow = "0 0 8px rgba(0,168,130,.2)"; }
              const canClick = eligible && !isPublic && !missingEmail;

              return (
                <div key={p.id} style={{ position: "relative" }} onMouseEnter={() => setHoveredCell(p.id)} onMouseLeave={() => setHoveredCell(null)}>
                  <button
                    disabled={!canClick}
                    onClick={() => { if (canClick) { setModalDates(["","",""]); setPyramidModal(p); } }}
                    style={{ width: CW, height: CH, background: bg, border, borderRadius: small ? 8 : 10, padding: 0, cursor: canClick ? "pointer" : "default", fontFamily: font, position: "relative", overflow: "hidden", boxSizing: "border-box", boxShadow: shadow, transition: "transform .1s, box-shadow .1s", transform: canClick && hoveredCell === p.id ? "scale(1.05)" : "scale(1)", WebkitTapHighlightColor: "transparent" }}>
                    <img src={p.photo || mkAvatar(p.name)} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: bs === "inativo" ? .2 : .85 }}/>
                    <div style={{ position: "absolute", top: small ? 2 : 4, right: small ? 3 : 5, fontFamily: fontCond, fontSize: small ? 11 : 13, fontWeight: 900, color: "#fff", textShadow: "0 1px 4px rgba(0,0,0,.8)" }}>{p.position}</div>
                    <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "linear-gradient(transparent,rgba(0,0,0,.85))", color: "#fff", fontSize: small ? 8 : 9, fontWeight: 700, padding: small ? "7px 2px 3px" : "8px 3px 4px", textAlign: "center", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{shortName(p.name)}</div>
                  </button>
                  {hoveredCell === p.id && (
                    <div style={{ position: "absolute", bottom: "105%", left: "50%", transform: "translateX(-50%)", background: C.card, color: C.text, borderRadius: 10, padding: "10px 14px", fontSize: 12, whiteSpace: "nowrap", zIndex: 999, boxShadow: "0 8px 24px rgba(0,0,0,.5)", pointerEvents: "none", border: "1px solid " + C.border }}>
                      <div style={{ fontWeight: 700, color: C.text, marginBottom: 4 }}>#{p.position} {p.name}</div>
                      {bs === "inativo"    && <div style={{ color: "#888" }}>Inativo{p.inactiveUntil ? " até " + new Date(p.inactiveUntil).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }) : ""}</div>}
                      {bs === "bloqueado"  && <div style={{ color: "#c0152a" }}>Indisponível{p.blockedAsChallenged ? " até " + new Date(p.blockedAsChallenged).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }) : ""}</div>}
                      {eligible && !isPublic && !missingEmail && <div style={{ color: C.accent, fontWeight: 700 }}>✓ Clique para desafiar</div>}
                      {eligible && !isPublic && missingEmail  && <div style={{ color: "#a07800", fontWeight: 700 }}>⚠️ Cadastre seu email para desafiar</div>}
                      {eligible && isPublic  && <div style={{ color: C.muted }}>Faça login para desafiar</div>}
                      {isMe && <div style={{ color: C.primary }}>← Você</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
